import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyTenants } from '@up2d8/contracts';
import { cn, useToast } from '@up2d8/ui';
import { useSession } from './session';

/**
 * Wisselen tussen bedrijven zonder opnieuw aan te melden (ACC-1).
 *
 * Alleen zichtbaar voor wie in meer dan één bedrijf een gekoppeld account heeft. Na het wisselen
 * gaat alle cache weg: wat van het ene bedrijf op het scherm stond, hoort niet een seconde lang in
 * het andere te staan.
 */
export function useMyTenants(enabled = true) {
  const { api, profile } = useSession();
  return useQuery({
    queryKey: ['my-tenants', profile?.accountId],
    enabled: enabled && Boolean(profile?.accountId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/tenants', {});
      return (data as MyTenants | undefined)?.items ?? [];
    },
  });
}

/** Wisselen: nieuw token, alle cache weg, terug naar het beginscherm van de zone. */
export function useSwitchTenant(zone: 'admin' | 'app') {
  const { t } = useTranslation();
  const { api, adoptToken } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  /** `to`: naar het beheer of de app van die organisatie (MST-10); standaard de huidige zone. */
  const switchTo = async (tenantCode: string, to: 'admin' | 'app' = zone) => {
    setBusy(true);
    try {
      const { data, error } = await api.POST('/api/v1/auth/switch-tenant', {
        body: { tenantCode } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      queryClient.clear();
      await adoptToken((data as { accessToken: string }).accessToken);
      void navigate({ to: to === 'admin' ? '/admin' : '/app' });
    } catch {
      toast.show({ title: t('tenantSwitch.failed'), tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return { switchTo, busy };
}

/**
 * Wisselen tussen bedrijven vanuit het logo en de bedrijfsnaam (ACC-1, ACC-3).
 *
 * Het merk linksboven - logo en naam - is de knop: daar staat waar je zit, en daar zoek je dus
 * ook hoe je ergens anders komt. Zo was het in de vorige app. Wie maar één bedrijf heeft, krijgt
 * het merk gewoon terug, zonder knop en zonder pijltje.
 */
export function TenantMenu({
  zone,
  tone,
  buttonClassName,
  chevron,
  single,
  children,
}: {
  zone: 'admin' | 'app';
  /** `dark` op de donkerblauwe zijbalk, `light` in de witte kopbalk van de telefoon. */
  tone: 'dark' | 'light';
  /**
   * De klassen van de knop in plaats van de standaardklassen; de beheerschil (BHR-0) geeft hier
   * `sidebar-org` uit de mockup.
   */
  buttonClassName?: string;
  /** Een eigen pijltje in plaats van het standaardpijltje. */
  chevron?: ReactNode;
  /** Wat er staat als er niets te wisselen valt; standaard het merk zelf. */
  single?: (children: ReactNode) => ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const items = useMyTenants().data ?? [];
  const current = items.find((item) => item.current);
  const { switchTo, busy } = useSwitchTenant(zone);
  const [open, setOpen] = useState(false);
  const { has, profile } = useSession();
  const navigate = useNavigate();
  // MST-10: vanuit de app ook naar het beheer (voor wie beheerder is) en naar Master.
  const toAdmin = zone === 'app' && has('access.role.manage');
  const toMaster = zone === 'app' && Boolean(profile?.platformAccess);
  const root = useRef<HTMLDivElement>(null);

  // Dicht bij een klik ernaast en bij Escape, zoals elk menu.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!current || (items.length < 2 && !toAdmin && !toMaster)) {
    return <>{single ? single(children) : children}</>;
  }

  return (
    <div className="relative min-w-0 flex-1" ref={root}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('tenantSwitch.current')} ${current.name}. ${t('tenantSwitch.choose')}`}
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
        data-testid="tenant-menu"
        className={
          buttonClassName ??
          cn(
            'flex w-full min-w-0 items-center gap-2 rounded-md text-left',
            tone === 'dark' ? 'hover:bg-white/10' : 'hover:bg-surface-sunk',
          )
        }
      >
        {children}
        {chevron ?? (
          <ChevronDown
            aria-hidden
            className={cn(
              'ml-auto h-4 w-4 shrink-0 transition-transform',
              open && 'rotate-180',
              tone === 'dark' ? 'text-white/70' : 'text-text-secondary',
            )}
          />
        )}
      </button>
      {open && (
        <ul
          role="menu"
          aria-label={t('tenantSwitch.label')}
          className="absolute left-0 z-50 mt-2 min-w-[14rem] rounded-lg border border-border bg-surface py-1 text-text-primary shadow-lg"
        >
          <li className="px-3 py-1 text-caption text-text-muted" role="presentation">
            {t('tenantSwitch.choose')}
          </li>
          {items.map((item) => (
            <li key={item.tenantCode} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={item.current}
                data-testid="tenant-option"
                onClick={() => {
                  setOpen(false);
                  if (!item.current) void switchTo(item.tenantCode);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-small hover:bg-surface-sunk"
              >
                <Check
                  aria-hidden
                  className={cn('h-4 w-4 shrink-0', item.current ? 'opacity-100' : 'opacity-0')}
                />
                <span className={cn('truncate', item.current && 'font-semibold')}>{item.name}</span>
              </button>
            </li>
          ))}
          {toAdmin || toMaster ? (
            <li role="separator" className="my-1 border-t border-border" />
          ) : null}
          {toAdmin ? (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid="tenant-menu-to-admin"
                onClick={() => {
                  setOpen(false);
                  void navigate({ to: '/admin' });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-small hover:bg-surface-sunk"
              >
                <span className="h-4 w-4 shrink-0" aria-hidden />
                {t('beheer.shell.toAdminOf', { name: current.name })}
              </button>
            </li>
          ) : null}
          {toMaster ? (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                data-testid="tenant-menu-to-master"
                onClick={() => {
                  setOpen(false);
                  void navigate({ to: '/master' });
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-small hover:bg-surface-sunk"
              >
                <span className="h-4 w-4 shrink-0" aria-hidden />
                {t('beheer.shell.toMaster')}
              </button>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

/** De keuzelijst op het profielscherm van de app; dezelfde wissel als het menu. */
export function TenantSwitcher({ zone }: { zone: 'admin' | 'app' }) {
  const { t } = useTranslation();
  const items = useMyTenants().data ?? [];
  const { switchTo, busy } = useSwitchTenant(zone);
  if (items.length < 2) return null;
  const current = items.find((item) => item.current);

  return (
    <label className="flex items-center gap-2 text-small text-text-secondary">
      <span className="sr-only">{t('tenantSwitch.label')}</span>
      <select
        className="h-9 rounded-pill border border-border bg-surface px-3 text-small font-medium text-text-primary"
        value={current?.tenantCode ?? ''}
        disabled={busy}
        onChange={(event) => {
          if (event.target.value !== current?.tenantCode) void switchTo(event.target.value);
        }}
        data-testid="tenant-switcher"
        aria-label={t('tenantSwitch.label')}
      >
        {items.map((item) => (
          <option key={item.tenantCode} value={item.tenantCode}>
            {item.name}
          </option>
        ))}
      </select>
    </label>
  );
}
